/**
 * Market index constants and calculations for the 3-Topf vs. 1-Topf Portfolio Monte-Carlo Simulation application
 * Contains historical market data for MSCI World and ACWI indices
 */

import {
  BaseStatistics,
  StatisticalCalculationService,
} from '../services/StatisticalCalculationService';

// Historische Index-Renditen in Prozent
export const INDEX_RETURNS = {
  2024: { world: 26.22, acwi: 25.64 },
  2023: { world: 19.49, acwi: 18.54 },
  2022: { world: -13.08, acwi: -12.88 },
  2021: { world: 29.79, acwi: 30.05 },
  2020: { world: 15.21, acwi: 15.53 },
  2019: { world: 27.3, acwi: 26.6 },
  2018: { world: -8.2, acwi: -9.5 },
  2017: { world: 23.07, acwi: 23.97 },
  2016: { world: 7.51, acwi: 7.86 },
  2015: { world: -0.87, acwi: -2.36 },
  2014: { world: 4.94, acwi: 4.16 },
  2013: { world: 26.68, acwi: 22.8 },
  2012: { world: 15.83, acwi: 16.13 },
  2011: { world: -5.54, acwi: -7.35 },
  2010: { world: 11.76, acwi: 12.67 },
  2009: { world: 29.99, acwi: 34.63 },
  2008: { world: -40.71, acwi: -42.19 },
  2007: { world: 9.04, acwi: 11.66 },
  2006: { world: 20.07, acwi: 20.73 },
  2005: { world: 9.49, acwi: 10.84 },
  2004: { world: 14.72, acwi: 15.25 },
  2003: { world: 33.76, acwi: 33.54 },
  2002: { world: -19.89, acwi: -19.5 },
  2001: { world: -16.52, acwi: -16.84 },
  2000: { world: -12.9, acwi: -13.18 },
  1999: { world: 24.93, acwi: 25.34 },
  1998: { world: 24.34, acwi: 24.8 },
  1997: { world: 15.98, acwi: 16.23 },
  1996: { world: 13.48, acwi: 14.0 },
  1995: { world: 20.81, acwi: 21.32 },
  1994: { world: 5.62, acwi: 5.56 },
  1993: { world: 22.78, acwi: 22.62 },
  1992: { world: -5.76, acwi: -6.18 },
  1991: { world: 18.72, acwi: 19.04 },
  1990: { world: -16.81, acwi: -17.01 },
  1989: { world: 16.86, acwi: 16.93 },
  1988: { world: 23.35, acwi: 23.39 },
  1987: { world: 16.17, acwi: 16.12 },
  1986: { world: 41.94, acwi: 41.85 },
  1985: { world: 40.64, acwi: 40.55 },
  1984: { world: 4.43, acwi: 4.48 },
  1983: { world: 23.7, acwi: 23.72 },
  1982: { world: 9.73, acwi: 9.71 },
  1981: { world: -1.92, acwi: -1.94 },
  1980: { world: 24.44, acwi: 24.42 },
  1979: { world: 13.69, acwi: 13.67 },
  1978: { world: 34.26, acwi: 34.28 },
  1977: { world: 2.4, acwi: 2.38 },
  1976: { world: 11.93, acwi: 11.95 },
  1975: { world: 35.7, acwi: 35.68 },
  1974: { world: -23.14, acwi: -23.16 },
  1973: { world: -14.69, acwi: -14.67 },
  1972: { world: 24.42, acwi: 24.44 },
  1971: { world: 19.44, acwi: 19.46 },
  1970: { world: -10.5, acwi: -10.48 },
} as const;

// Index statistics interface
export interface IndexStatistics extends BaseStatistics {}

export const getIndexStatistics = (index: 'world' | 'acwi' = 'world'): IndexStatistics => {
  return StatisticalCalculationService.calculateIndexStatistics(INDEX_RETURNS, index);
};

// Backward compatibility - defaults to MSCI World
export const getMSCIWorldStatistics = (): IndexStatistics => {
  return getIndexStatistics('world');
};

// Hilfsfunktionen für Index-Daten
export const getIndexReturn = (year: number, index: 'world' | 'acwi' = 'world'): number | null => {
  const data = INDEX_RETURNS[year as keyof typeof INDEX_RETURNS];
  return data ? data[index] : null;
};

export const getAverageIndexReturn = (
  startYear: number,
  endYear: number,
  index: 'world' | 'acwi' = 'world'
): number | null => {
  const returns = [];
  for (let year = startYear; year <= endYear; year++) {
    const returnRate = getIndexReturn(year, index);
    if (returnRate !== null) {
      returns.push(returnRate);
    }
  }

  if (returns.length === 0) {return null;}

  return returns.reduce((sum, rate) => sum + rate, 0) / returns.length;
};

export const getIndexYears = (): number[] => {
  return Object.keys(INDEX_RETURNS)
    .map(year => parseInt(year))
    .sort((a, b) => b - a);
};

export const getIndexVolatility = (
  startYear: number,
  endYear: number,
  index: 'world' | 'acwi' = 'world'
): number | null => {
  const returns = [];
  for (let year = startYear; year <= endYear; year++) {
    const returnRate = getIndexReturn(year, index);
    if (returnRate !== null) {
      returns.push(returnRate);
    }
  }

  if (returns.length < 2) {return null;}

  const mean = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
  // Stichproben-Varianz ÷(n−1) statt Populations-Varianz ÷N: konsistent mit dem
  // uebrigen (durchgaengig ÷(n−1)) Code, u.a. StatisticalCalculationService. Die
  // Index-Renditen sind eine Stichprobe realisierter Jahre, keine Grundgesamtheit.
  const variance =
    returns.reduce((sum, ret) => sum + Math.pow(ret - mean, 2), 0) / (returns.length - 1);

  return Math.sqrt(variance);
};

export const getIndexStats = (
  startYear: number,
  endYear: number,
  index: 'world' | 'acwi' = 'world'
) => {
  const returns = [];
  for (let year = startYear; year <= endYear; year++) {
    const returnRate = getIndexReturn(year, index);
    if (returnRate !== null) {
      returns.push(returnRate);
    }
  }

  if (returns.length === 0) {
    return {
      mean: 0,
      volatility: 0,
      min: 0,
      max: 0,
      positiveYears: 0,
      negativeYears: 0,
      totalYears: 0,
    };
  }

  const mean = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
  // Stichproben-Varianz ÷(n−1) (siehe getIndexVolatility). Bei n=1 waere der Nenner 0;
  // in dem Fall ist die Volatilitaet mangels Streuung mit 0 definiert.
  const variance =
    returns.length > 1
      ? returns.reduce((sum, ret) => sum + Math.pow(ret - mean, 2), 0) / (returns.length - 1)
      : 0;
  const volatility = Math.sqrt(variance);

  return {
    mean,
    volatility,
    min: Math.min(...returns),
    max: Math.max(...returns),
    positiveYears: returns.filter(r => r > 0).length,
    negativeYears: returns.filter(r => r < 0).length,
    totalYears: returns.length,
  };
};
